var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ButtonLink } from '@marketing/components/global/CTA/Button';
import Heading from '@marketing/components/global/typography/Heading';
import FlexSection from '@marketing/components/sections/FlexSection';
import SingleNomSVG from '@svgs/global/NomNomLogo_SingleNom.svg?url';
import { useEffect, useRef, useState } from 'react';
function lerp(start, end, amount) {
    return Math.min(end, Math.max(start, (1 - amount) * start + amount * end));
}
// logit(x) is an inverse sigmoid function, a nice s-shaped curve
// that maps (0, 1) to (-inf, +inf)
function logit(x) {
    return Math.log(x / (1 - x));
}
function DiscountChomp(_a) {
    var _b = _a.biteScrollRange, biteScrollRange = _b === void 0 ? 0.25 : _b, _c = _a.biteStart, biteStart = _c === void 0 ? 0 : _c, _d = _a.splatFactor, splatFactor = _d === void 0 ? 0.7 : _d, _e = _a.biteStrength, biteStrength = _e === void 0 ? 1 : _e, _f = _a.biteResistance, biteResistance = _f === void 0 ? 6 : _f;
    var _g = useState(0), scrollProgress = _g[0], setScrollProgress = _g[1];
    var _h = useState(undefined), originalComponentHeight = _h[0], setOriginalComponentHeight = _h[1];
    var componentRef = useRef(null);
    var discountRef = useRef(null);
    var lerpedBiteStart = lerp(0, 1, biteStart);
    var lerpedBiteScrollRange = lerp(0, 1, biteScrollRange);
    var lerpedBiteStrength = lerp(0, 10, biteStrength);
    var lerpedBiteResistance = lerp(0, 10, biteResistance);
    var lerpedSplatFactor = lerp(0, 1, splatFactor);
    useEffect(function () {
        var handleScroll = function () {
            if (componentRef.current && discountRef.current) {
                if (isNaN(originalComponentHeight)) {
                    setOriginalComponentHeight(discountRef.current.getBoundingClientRect().height);
                }
                var windowHeight = window.innerHeight;
                var componentTop = Math.max(0, windowHeight - componentRef.current.getBoundingClientRect().top);
                // progress is defined as how far into the biteScrollRange window (plus
                // biteStart) the top of the NomNom logo is
                var progress = Math.min(1, Math.max(0, (componentTop - windowHeight * lerpedBiteStart - (windowHeight * biteScrollRange) / 2) /
                    (windowHeight * lerpedBiteScrollRange)));
                // this curve gives the bite a 'chomp' effect, rather than just a linear
                // closing/opening
                setScrollProgress(Math.pow(progress, 1 / lerpedBiteStrength));
            }
        };
        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return function () { return window.removeEventListener('scroll', handleScroll); };
    }, [originalComponentHeight]);
    function scaledLogit(x) {
        // clamp logit(x) down from (-inf, +inf) to (0, splatFactor)
        // so that we can use it to scale the chomping 'splat' effect
        return Math.min(1, Math.max(0, logit(x) / lerpedBiteResistance + 0.5));
    }
    // tailwind doesn't handle dynamic styles well, so we have to do this
    // manually
    var styles = originalComponentHeight
        ? {
            transform: "scaleY(".concat(scrollProgress, ") scaleX(").concat(1 / scaledLogit(Math.pow(scrollProgress, lerpedSplatFactor * splatFactor)), ")"),
            opacity: scrollProgress * 2,
            marginTop: (-1 * originalComponentHeight) / 2 + (originalComponentHeight / 2) * scrollProgress,
            marginBottom: (-1 * originalComponentHeight) / 2 + (originalComponentHeight / 2) * scrollProgress,
        }
        : {};
    return (_jsxs(FlexSection, __assign({ backgroundColorStyle: "bg-action-surface-primary", gapStyle: "gap-2xs-m md:gap-lg-gutter", paddingStyle: "px-2xs-gutter py-sm-gutter md:px-4xl-gutter md:py-lg-gutter" }, { children: [_jsxs("div", __assign({ className: "flex flex-col items-center gap-xs-m shrink-0" }, { children: [_jsx(Heading, __assign({ size: "sm", className: "text-center" }, { children: "Try" })), _jsxs("div", __assign({ ref: componentRef, className: "flex flex-col justify-center h-[100px] min-h-[140px] md:min-h-[183px]" }, { children: [_jsx("img", { src: SingleNomSVG, className: "z-10 bg-action-surface-primary shrink-0 w-[152px] object-fill md:w-[201px] pb-[3px]", alt: "Single Nom" }), _jsx("div", __assign({ ref: discountRef, style: styles }, { children: _jsx(Heading, __assign({ size: "sm", className: "text-center text-text-body uppercase leading-[90%]" }, { children: "for 50% off" })) })), _jsx("img", { src: SingleNomSVG, className: "z-10 bg-action-surface-primary shrink-0 w-[152px] object-fill md:w-[201px] md:h-[66px]", alt: "Single Nom" })] }))] })), _jsxs("div", __assign({ className: "flex flex-col items-center md:items-start gap-xl-m shrink-0 md:w-[385px]" }, { children: [_jsx("p", __assign({ className: "text-center md:text-left" }, { children: "Sign up for mouthwatering meals, delivered to your door, and get ready to see your pup\u2019s Nom Face.*" })), _jsx(ButtonLink, __assign({ href: "/profile", size: "small", use: "secondary" }, { children: "Get Started" })), _jsx("p", __assign({ className: "text-center md:text-left text-sm-med-m md:text-sm-med-d" }, { children: "*Nomspeak for dogs deliriously happy about dinner." }))] }))] })));
}
export default DiscountChomp;
